import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as currenciesApi from './currencies.api';
import { fetchFxRate, type FxRateResponse } from './fx.api';

import type {
  CreateCurrencyRequest,
  CurrencyListQuery,
  UpdateCurrencyRequest,
} from '@utazzo/shared';

const QK = {
  list: (query: CurrencyListQuery) => ['currencies', 'list', query] as const,
  detail: (id: string) => ['currencies', 'detail', id] as const,
};

export const useCurrenciesList = (query: CurrencyListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => currenciesApi.listCurrencies(query),
    staleTime: 30_000,
  });

/**
 * Fetch the FX multiplier from `from` currency to `to` currency. Skip the
 * network round-trip when both codes are equal (returns 1). Caches for an hour
 * client-side; the server keeps its own 6h Redis cache on top, so multiple
 * consumers of the same pair effectively coalesce to one provider hit.
 */
export const useFxRate = (from: string | null | undefined, to: string | null | undefined) => {
  const f = (from ?? '').toUpperCase().trim();
  const t = (to ?? '').toUpperCase().trim();
  const skip = !f || !t || f === t;
  return useQuery<FxRateResponse>({
    queryKey: ['fx-rate', f, t],
    queryFn: () => (skip ? Promise.resolve({ from: f, to: t, rate: 1 }) : fetchFxRate(f, t)),
    staleTime: 60 * 60 * 1000,
    enabled: true,
  });
};

export const useCreateCurrency = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCurrencyRequest) => currenciesApi.createCurrency(payload),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ['currencies', 'list'] });
      toast.success(`Added ${c.code}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add currency'),
  });
};

export const useUpdateCurrency = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateCurrencyRequest }) =>
      currenciesApi.updateCurrency(id, payload),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ['currencies', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(c.id) });
      toast.success('Currency updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update currency'),
  });
};

export const useDeleteCurrency = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => currenciesApi.deleteCurrency(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['currencies', 'list'] });
      toast.success('Currency deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete currency'),
  });
};

export const useBulkDeleteCurrencies = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => currenciesApi.bulkDeleteCurrencies(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['currencies', 'list'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete currencies'),
  });
};

export const useBulkSetCurrenciesStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, isActive }: { ids: string[]; isActive: boolean }) =>
      currenciesApi.bulkSetCurrenciesStatus(ids, isActive),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['currencies', 'list'] }),
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to update currency status'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
